import { FC, useEffect, useState, useMemo } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import { FaInfoCircle, FaTable, FaCalendarAlt } from 'react-icons/fa';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router';
import { buildFilters } from 'components/table/utils/filtersConfig';
import { ISelectOptionProps } from 'components/form/types';
import { useActiveClientContext } from 'store/active-client';
import { buildClientEntity } from 'utils/helpers/client';
import { useTranslation } from 'react-i18next';
import { getSubdomain } from 'utils/helpers/routing';
import { setAuthTokensFromResponse, getUserInfo } from 'utils/auth';
import { updateUserData } from 'utils/userDataSync';
import API from 'core/Api';
import { useLanguageContext } from 'store/language';
import { useWorkModeContext } from 'store/work-mode';
import { usePermissionsContext } from 'store/permissions';
import { useModal } from 'store/modal';
import styled from 'styled-components';
import { formatDate } from 'utils/helpers/formatters';
import { calendarLegendItemsColleague, calendarLegendHomeOffice } from 'utils/helpers/leaves';

import NotAuthorizedScreen from 'routes/not-authorized';
import MainLayout from 'components/main-layout/MainLayout';
import TooltipWrapper from 'components/TooltipWrapper';
import Button from 'components/Button';

import UpcomingLeaves from './components/UpcomingLeaves';
import Calendar from './components/Calendar';
import ColleagueLeavesGrid from './components/ColleagueLeavesGrid';

const Screen: FC = () => {
  const [showAttendant, setShowAttendant] = useState(false);
  const [showGridView, setShowGridView] = useState<boolean>(false);
  const [date, setDate] = useState<Date>(new Date());
  const { t } = useTranslation();
  const isOperator = getSubdomain() === 'operators';
  const { language } = useLanguageContext();
  const { featureLeavesIsActive, featureColleaguesLeaves } = getUserInfo();
  const isColleagueLeavesFeatureActive = featureLeavesIsActive && featureColleaguesLeaves;
  const { client } = useActiveClientContext();
  const { setPermissions } = usePermissionsContext();
  const { showModal } = useModal();
  const history = useHistory();
  const { altWorkModeName, setWorkMode, setWorkModeName, setAltWorkModeName } = useWorkModeContext();

  const {
    isLoading: isLoadingLeavesDropdowns,
    error: errorDropdowns,
    data: dropdowns,
  } = useQuery<{ [key: string]: ISelectOptionProps[] }, Error>(
    [buildClientEntity('leaves-colleagues-dropdowns', client)],
    (): Promise<{ [key: string]: ISelectOptionProps[] }> => {
      return API.get('/leaves/colleagues/dropdown-options/').then(res => {
        const filters = res.data && res.data[language || 'bg'];
        const hasAttendantLeaves = res.data?.has_attendant_leaves;
        return {
          ...filters,
          has_attendant_leaves: hasAttendantLeaves,
        };
      });
    },
    {
      cacheTime: 0,
    }
  );

  const calculateRangeDates = () => {
    const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const startDayPosition = startDate.getDay();
    const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const endtDayPosition = endDate.getDay();

    startDate.setDate(startDate.getDate() - (startDayPosition - 1));
    endDate.setDate(endDate.getDate() + (7 - endtDayPosition));

    return {
      startDate: formatDate(startDate, 'YYY-mm-dd'),
      endDate: formatDate(endDate, 'YYY-mm-dd'),
    };
  };

  const { startDate, endDate } = calculateRangeDates();
  const year = date.getFullYear();

  const {
    isLoading: isLoadingGridLeaves,
    error: errorGridLeaves,
    data: calendarEventsData = [],
  } = useQuery<any[], Error>(
    [buildClientEntity('colleague-leaves-calendar', client), { startDate, endDate, showAttendant }],
    (): Promise<any[]> => {
      return API.get(
        `leaves/colleagues/calendar?limit=1000&offset=0&date_from=${startDate}&date_to=${endDate}&show_attendant=${showAttendant}`
      ).then(res => {
        return res.data?.items?.map((leave: any) => ({
          ...leave,
          start: new Date(leave.date_from).setHours(0, 0, 0, 0),
          end: new Date(leave.date_to).setHours(23, 59, 59, 999),
        }));
      });
    },
    {
      enabled: showGridView,
    }
  );

  const {
    isLoading: isLoadingHolidays,
    error: errorHolidays,
    data: holidaysData = [],
  } = useQuery<any[], Error>(
    [buildClientEntity('holidays', client), year],
    (): Promise<any[]> => {
      return API.get(`holidays/${year}`).then(res => {
        return res.data?.items?.map((holiday: any, index: number) => {
          const start = new Date(holiday.date);
          start.setHours(0, 0, 0, 0);
          const end = new Date(holiday.date);
          end.setHours(23, 59, 59, 999);

          return {
            ...holiday,
            id: index,
            title: holiday.name,
            start,
            end,
            type: 'holiday',
            allDay: true,
          };
        });
      });
    },
    {
      enabled: showGridView,
    }
  );

  const toggleWorkMode = () => {
    API.post('auth/work-mode-change')
      .then(res => setAuthTokensFromResponse(res))
      .then(() => API.get('permissions'))
      .then(res => {
        setPermissions(res.data);
      })
      .then(() =>
        updateUserData({
          setWorkMode: mode => setWorkMode(mode, history),
          setWorkModeName,
          setAltWorkModeName,
          onSuccess: data => {
            // Show success switched mode message
            showModal({
              type: 'success',
              content: (
                <>
                  {t('common.switchedMode')}&nbsp;
                  <span className="font-weight-bold">{data.work_mode_name}</span>.
                </>
              ),
            });
          },
        })
      )
      .then(() => {
        history.push('/leaves');
      })
      .catch(err => console.error(err));
  };

  const handleToggle = (e: any) => {
    const isChecked = e.target.checked;
    setShowAttendant(isChecked);
  };

  const tableFilters = useMemo(
    () => [
      buildFilters({
        name: 'leave_period_start',
        placeholder: t('leaves.dateFrom'),
        type: 'monthyearpickerstart',
      }),
      buildFilters({
        name: 'leave_period_end',
        placeholder: t('leaves.dateTo'),
        type: 'monthyearpickerend',
        subType: 'leaveNextMonth',
      }),
      buildFilters({
        name: 'status',
        placeholder: t('table.statusFilter'),
        options: dropdowns?.status,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'user',
        placeholder: t('documents.table.employee'),
        options: dropdowns?.user,
        isMulti: true,
        limitOptions: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'group',
        placeholder: t('table.groupFilter'),
        options: dropdowns?.group,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'show_attendant',
        placeholder: t('leaves.type'),
        options: [
          { label: t('leaves.nonAttendant'), value: 'false', selected: !showAttendant },
          { label: t('leaves.attendant'), value: 'true', selected: showAttendant },
        ],
        isMulti: false,
        showTotal: false,
        allowSelectAll: false,
        hidden: true,
      }),
    ],
    [dropdowns, showAttendant]
  );

  useEffect(() => {}, []);

  if (isColleagueLeavesFeatureActive || !isOperator) {
    return (
      <>
        <MainLayout
          title={t('leaves.colleagueTitle')}
          isLoading={isLoadingLeavesDropdowns}
          error={errorDropdowns}
        >
          <ButtonsContainer>
            <TooltipWrapper
              id="tooltip-toggle-view"
              overlay={
                showGridView
                  ? `${t('leaves.switchTo')} ${t('leaves.showCalendarView')}`
                  : `${t('leaves.switchTo')} ${t('leaves.showGridView')}`
              }
            >
              <ToggleButton
                btnType="secondary"
                onClick={() => setShowGridView(prev => !prev)}
              >
                {!showGridView ? (
                  <>
                    <FaCalendarAlt
                      size="16"
                      className="mr-2"
                    />
                    {t('leaves.showCalendarView')}
                  </>
                ) : (
                  <>
                    <FaTable
                      size="16"
                      className="mr-2"
                    />
                    {t('leaves.showGridView')}
                  </>
                )}
              </ToggleButton>
            </TooltipWrapper>
            {dropdowns?.has_attendant_leaves && (
              <SwitchWrapper>
                <TooltipWrapper
                  id="tooltip-trash"
                  overlay={t('leaves.attendantTooltip')}
                >
                  <div className="d-flex align-items-center">
                    <span>{t('leaves.leaves')}</span>
                    <Form.Check
                      custom
                      className="custom-switch-control"
                      type="switch"
                      id="show-attendant-switch"
                      checked={showAttendant}
                      onChange={handleToggle}
                    />
                    <span>{t('leaves.otherLeaves')}</span>
                  </div>
                </TooltipWrapper>
              </SwitchWrapper>
            )}
          </ButtonsContainer>
          {tableFilters && (
            <>
              {!showGridView ? (
                <div className="mb-4">
                  <Row>
                    <Col md={5}>
                      <div style={{ position: 'relative', zIndex: 5 }}>
                        <UpcomingLeaves filters={tableFilters} />
                      </div>
                    </Col>
                    <Col md={7}>
                      <Calendar showAttendant={showAttendant} />
                    </Col>
                  </Row>
                </div>
              ) : (
                <>
                  <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
                    <TooltipWrapper
                      id="tooltip-legend"
                      overlay={t('leaves.legend.tooltip')}
                    >
                      <CalendarLegend>
                        {(showAttendant ? calendarLegendHomeOffice : calendarLegendItemsColleague).map(item => (
                          <LegendItem
                            key={item.type}
                            style={{
                              backgroundColor: `${item.backgroundColor}`,
                              color: item.color || '#fff',
                            }}
                          >
                            {t(`leaves.legend.${item.type}`)}
                          </LegendItem>
                        ))}
                      </CalendarLegend>
                    </TooltipWrapper>
                  </div>
                  <div className="mb-4">
                    <ColleagueLeavesGrid
                      leavesData={calendarEventsData}
                      holidaysData={holidaysData}
                      date={date}
                      onDateChange={setDate}
                      isLoading={isLoadingGridLeaves || isLoadingHolidays}
                    />
                  </div>
                  <div className="mb-4">
                    <UpcomingLeaves filters={tableFilters} />
                  </div>
                </>
              )}
              {altWorkModeName && (
                <i>
                  <FaInfoCircle className="mr-1 mb-1" />
                  {t('leaves.colleagueInfoMessage')}{' '}
                  <div
                    onClick={toggleWorkMode}
                    style={{ color: '#716aca', cursor: 'pointer', display: 'inline' }}
                  >
                    {t('leaves.switchWorkMode', { workmode: altWorkModeName })}
                  </div>
                </i>
              )}
            </>
          )}
        </MainLayout>
      </>
    );
  }

  return <NotAuthorizedScreen />;
};

export default Screen;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const ToggleButton = styled(Button)`
  min-width: 180px;

  @media (max-width: 768px) {
    min-width: 100% !important;
  }
`;

const CalendarLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const LegendItem = styled.div`
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`;

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }

  .custom-switch-control {
    padding-top: 6px;
    padding-bottom: 6px;
    margin-left: 6px;

    .custom-control-label {
      &::before {
        height: 1.25rem;
      }
    }

    .custom-control-input:checked ~ .custom-control-label {
      &::after {
        transform: translateX(1.45rem);
      }
    }
  }

  .custom-switch-control {
    .custom-control-input:checked ~ .custom-control-label::before {
      background-color: #fd7e14; // ON state background color
      border-color: #fd7e14; // ON state border color
    }

    .custom-control-input ~ .custom-control-label::before {
      background-color: #32bfa3; // OFF state background color
    }

    .custom-control-label::after {
      background-color: #fff; // Thumb color
    }
  }
`;
